import type { ReactNode } from 'react';
import { BRAND_LOGO, Logo, SkipToContent, Surface } from '@beautyburst/ui';
import { minTouchTarget, semanticColor, space } from '@beautyburst/tokens';
import type {
  AfterHoursEnrollment,
  RetailerApiClient,
  RetailerDashboard,
  RetailerOrderSummary,
  RetailerPayoutOverview,
  RetailerStoreProfile,
  JsonRecord,
} from './api-client.ts';
import { allowedNavRoutes, DesktopNav, MobileBottomNav } from './navigation.tsx';
import { PartnerScreen } from '../../web-customer/src/screens/partner.tsx';
import {
  AfterHoursScreen,
  CatalogScreen,
  DashboardScreen,
  InventoryScreen,
  ImportsScreen,
  NotFoundScreen,
  OrderDetailScreen,
  OrdersScreen,
  PayoutsScreen,
  SignInScreen,
  StoreProfileScreen,
} from './screens.tsx';
import { RetailerSecurityScreen } from './mfa.tsx';
import { resolveRoute, RETAILER_ROUTES, type RouteContract, type RouteMatch } from './routes.ts';
import { retailerAsset, retailerHref } from './base-path.ts';

export interface RetailerActor {
  signedIn: boolean;
  displayName?: string;
  permissions: ReadonlySet<string>;
}

export interface RetailerPortalData {
  dashboard?: RetailerDashboard;
  orders?: readonly RetailerOrderSummary[];
  selectedOrder?: RetailerOrderSummary;
  inventory?: readonly JsonRecord[];
  storeProfile?: RetailerStoreProfile;
  afterHours?: AfterHoursEnrollment;
  payouts?: RetailerPayoutOverview;
}

export interface AppProps {
  path: string;
  client: RetailerApiClient;
  flags: ReadonlySet<string>;
  actor: RetailerActor | null;
  logoSrc?: string;
  data?: RetailerPortalData;
  renderScreen?: (match: RouteMatch<RouteContract>) => ReactNode;
  onSignedIn?: () => void;
  onRequestStepUp?: () => Promise<void>;
}

function Unavailable({ screen }: { screen: string }) {
  return (
    <section aria-labelledby="retailer-unavailable-title">
      <h1 id="retailer-unavailable-title">Not available yet</h1>
      <p>
        {screen} is not available in the retailer portal yet. It is not shown in the portal
        navigation.
      </p>
    </section>
  );
}

function AccessDenied() {
  return (
    <section aria-labelledby="retailer-access-title">
      <h1 id="retailer-access-title">You do not have access to this area</h1>
      <p>Ask a retailer owner to update your account permissions if you need this capability.</p>
    </section>
  );
}

function SignInPrompt() {
  return (
    <section aria-labelledby="retailer-auth-title">
      <h1 id="retailer-auth-title">Sign in to continue</h1>
      <p>Sign in with your retailer account to access store operations.</p>
      <a href={retailerHref('/sign-in')}>Go to retailer sign in</a>
    </section>
  );
}

/**
 * Client-side checks are defense in depth and navigation hygiene only. Every
 * API request must independently enforce its scoped permission and ownership.
 */
export function App({
  path,
  client,
  flags,
  actor,
  logoSrc = retailerAsset(BRAND_LOGO.mark),
  data = {},
  renderScreen,
  onSignedIn,
  onRequestStepUp,
}: AppProps) {
  const match = resolveRoute(path);
  const route = match?.route ?? null;
  const navigation = allowedNavRoutes(RETAILER_ROUTES, flags, actor);

  let content: ReactNode;
  if (route === null) {
    content = <NotFoundScreen />;
  } else if (!flags.has(route.flag)) {
    content = <Unavailable screen={route.screen} />;
  } else if (!route.available) {
    content = <Unavailable screen={route.screen} />;
  } else if (route.permission !== null && actor?.signedIn !== true) {
    content = <SignInPrompt />;
  } else if (route.permission !== null && actor?.permissions.has(route.permission) !== true) {
    content = <AccessDenied />;
  } else if (
    route.screen !== 'Sign in' &&
    route.screen !== 'Retailer partnership' &&
    match !== null &&
    renderScreen !== undefined
  ) {
    content = renderScreen(match);
  } else {
    switch (route.screen) {
      case 'Sign in':
        content = <SignInScreen client={client} onSignedIn={onSignedIn} />;
        break;
      case 'Retailer partnership':
        content = <PartnerScreen client={client} />;
        break;
      case 'Dashboard':
        content = <DashboardScreen dashboard={data.dashboard} />;
        break;
      case 'Orders':
        content = <OrdersScreen orders={data.orders} />;
        break;
      case 'Order detail':
        content = <OrderDetailScreen order={data.selectedOrder} client={client} />;
        break;
      case 'Catalog':
        content = <CatalogScreen client={client} />;
        break;
      case 'Inventory':
        content = <InventoryScreen balances={data.inventory} client={client} />;
        break;
      case 'Imports':
        content = <ImportsScreen client={client} />;
        break;
      case 'Store profile':
        content = <StoreProfileScreen profile={data.storeProfile} client={client} />;
        break;
      case 'Beauty After Hours':
        content = <AfterHoursScreen enrollment={data.afterHours} client={client} />;
        break;
      case 'Payouts':
        content = <PayoutsScreen overview={data.payouts} />;
        break;
      case 'Security':
        content = (
          <RetailerSecurityScreen
            client={client.mfa}
            onRequestStepUp={onRequestStepUp ?? (async () => undefined)}
          />
        );
        break;
      default:
        content = <NotFoundScreen />;
    }
  }

  return (
    <Surface surface="retailer">
      <style>{`button { min-width: ${minTouchTarget}; min-height: ${minTouchTarget}; }`}</style>
      <SkipToContent />
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: space[4],
          minHeight: '64px',
          padding: `0 ${space[4]}`,
          background: semanticColor.surfaceCard,
          borderBottom: `1px solid ${semanticColor.borderDefault}`,
        }}
      >
        <Logo src={logoSrc} variant="mark" />
        <DesktopNav activeNavKey={route?.navKey ?? null} routes={navigation} />
      </header>
      <main
        id="main"
        tabIndex={-1}
        style={{ maxWidth: '78rem', margin: '0 auto', padding: space[5] }}
      >
        {content}
      </main>
      <MobileBottomNav activeNavKey={route?.navKey ?? null} routes={navigation} />
    </Surface>
  );
}
